import { useState, useEffect } from 'react';
import { Play, AlertTriangle, AlertCircle, Instagram } from 'lucide-react';
import { videoApi } from '@/services/api';
import { parseMediaUrl } from './parseMediaUrl';
import { UploadedVideoPlayer } from './UploadedVideoPlayer';
import { InstagramCarouselPreview } from './InstagramCarouselPreview';
import { Button } from '@/components/ui/Button';
import { BrandMark, BrandLoader } from '@/components/ui/brand-loader';
import { getDisplayErrorMessage } from '@/utils';

import { useTranslation } from 'react-i18next';
interface InstagramPreviewProps {
  url?: string;
  mediaUrl?: string;
  mediaKind?: 'image' | 'video';
  caption?: string;
  hashtags?: string[]
  username?: string;
  profileImageUrl?: string | null;
  boardId?: string;
  // Pre-fetched by parent (CreateCardModal flow)
  videoUrl?: string | null;
  thumbnailUrl?: string | null;
  isLoading?: boolean;
  error?: string | null;
  // Content type: 'reels' | 'photo' | 'carousel'
  contentType?: string;
  // For carousel: the list of uploaded (blob or MinIO) URLs
  carouselUrls?: string[];
  /** Overrides the empty-state line, e.g. for upload-only surfaces where "Add Instagram URL" is wrong. */
  emptyLabel?: string;
}

/**
 * Instagram preview — adapts to the selected content type.
 *
 * reels    → 9:16 portrait (default)
 * photo    → 1:1 square
 * carousel → 1:1 square with InstagramCarouselPreview + dots
 */
export function InstagramPreview({
  url,
  mediaUrl,
  mediaKind,
  caption,
  hashtags,
  username = 'your_account',
  profileImageUrl,
  boardId,
  videoUrl: initialVideoUrl,
  thumbnailUrl: initialThumbnail,
  isLoading,
  error: initialError,
  contentType = 'reels',
  carouselUrls = [],
  emptyLabel,
}: InstagramPreviewProps) {
    const { t } = useTranslation();
  const parsePreviewMedia = (nextUrl?: string, nextMediaUrl?: string) => {
    if (nextMediaUrl?.startsWith('blob:') && mediaKind) {
      return { type: mediaKind, directUrl: nextMediaUrl } as ReturnType<typeof parseMediaUrl>;
    }
    return parseMediaUrl(nextUrl, nextMediaUrl);
  };

  const [parsed, setParsed] = useState(() => parsePreviewMedia(url, mediaUrl));

  // Extraction / playback state (owned internally)
  const [extractedVideoUrl, setExtractedVideoUrl] = useState<string | null>(initialVideoUrl ?? null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(initialThumbnail ?? null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(initialError ? getDisplayErrorMessage(initialError, 'Could not load preview') : null);
  const [mediaError, setMediaError] = useState(false);

  useEffect(() => {
    const next = parsePreviewMedia(url, mediaUrl);
    setParsed(next);
    // Reset playback state when URL changes
    setExtractedVideoUrl(initialVideoUrl ?? null);
    setThumbnailUrl(initialThumbnail ?? null);
    setIsPlaying(false);
    setVideoError(initialError ? getDisplayErrorMessage(initialError, 'Could not load preview') : null);
    setMediaError(false);
  }, [url, mediaUrl, mediaKind, initialVideoUrl, initialThumbnail, initialError]);

  // Aspect ratio driven by content type
  const isSquare = contentType === 'photo' || contentType === 'carousel';
  const aspect = isSquare ? 'aspect-square' : 'aspect-[9/16]';
  const base = `${aspect} w-full rounded-lg overflow-hidden relative transition-all duration-300`;

  /** Instagram-style bottom overlay */
  const Overlay = () => (
    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none z-10">
      <div className="flex items-center gap-2 mb-1.5">
        {profileImageUrl ? (
          <img src={profileImageUrl} alt={username} className="size-7 rounded-full object-cover ring-2 ring-gradient-to-tr from-yellow-400 to-pink-500" />
        ) : (
          <div className="size-7 rounded-full bg-gradient-to-tr from-yellow-400 to-pink-600 p-[1.5px]">
            <div className="w-full h-full rounded-full bg-black flex items-center justify-center text-white text-[10px] font-bold">
              {username.charAt(0).toUpperCase()}
            </div>
          </div>
        )}
        <span className="text-xs font-semibold text-white">@{username}</span>
      </div>
      {caption && <p className="text-xs text-white/90 line-clamp-2 leading-snug">{caption}</p>}
      {hashtags && hashtags.length > 0 && (
        <p className="text-xs text-blue-300 mt-1">{hashtags.slice(0, 3).map(t => `#${t}`).join(' ')}</p>
      )}
    </div>
  );

  const handlePlay = async () => {
    if (extractedVideoUrl) { setIsPlaying(true); return; }
    if (!boardId || !url) return;

    setIsExtracting(true);
    setVideoError(null);
    try {
      const result = await videoApi.extractVideo(url, boardId);
      if (result.video_url) {
        setExtractedVideoUrl(videoApi.getProxyUrl(result.video_url));
        if (result.thumbnail_url) setThumbnailUrl(result.thumbnail_url);
        setIsPlaying(true);
      } else {
        setVideoError(getDisplayErrorMessage(result.error, 'Could not extract video'));
      }
    } catch {
      setVideoError('Failed to extract video');
    } finally {
      setIsExtracting(false);
    }
  };

  // Loading (parent-driven)
  if (isLoading) {
    return (
      <div className={`${base} bg-black flex items-center justify-center`}>
        <div className="flex flex-col items-center gap-3">
          <BrandMark size={40} variant="loading" />
          <p className="text-white/60 text-xs">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // ── CAROUSEL ──
  if (contentType === 'carousel') {
    if (carouselUrls.length > 0) {
      // Show real carousel with slides
      return (
        <div className={`${aspect} w-full rounded-lg overflow-hidden relative`}>
          <InstagramCarouselPreview
            urls={carouselUrls}
            getProxyUrl={videoApi.getProxyUrl}
          />
        </div>
      );
    }
    if (thumbnailUrl && !mediaError) {
      return (
        <div className={`${base} bg-black`}>
          <img
            src={thumbnailUrl}
            alt={t('preview.carouselThumbnail')}
            className="absolute inset-0 w-full h-full object-cover"
            onError={() => setMediaError(true)}
          />
          <div className="absolute top-3 right-3 z-20 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-semibold text-white backdrop-blur">
            Carousel
          </div>
          <Overlay />
        </div>
      );
    }
    // Placeholder for carousel (no files yet)
    return (
      <div className={`${base} bg-gradient-to-b from-purple-900/30 via-pink-900/20 to-gray-900 flex flex-col items-center justify-center gap-3 group cursor-pointer`}>
        <div className="size-16 rounded-2xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
          <Instagram className="size-8 text-white" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-gray-300">{t('preview.addCarouselFiles')}</p>
          <p className="text-xs text-gray-500 mt-0.5">1:1 · Upload multiple images/videos</p>
        </div>
        <Overlay />
      </div>
    );
  }

  // ── PHOTO ──
  if (contentType === 'photo') {
    if (parsed.type === 'image' && parsed.directUrl && !mediaError) {
      return (
        <div className={`${base} bg-black`}>
          <img src={parsed.directUrl} alt={t('preview.uploaded')} className="w-full h-full object-cover" onError={() => setMediaError(true)} />
          <Overlay />
        </div>
      );
    }
    // video uploaded but mode is photo — show image frame
    if (parsed.type === 'video' && parsed.directUrl && !mediaError) {
      return (
        <div className={`${base} bg-black group cursor-pointer`} onClick={() => setIsPlaying(true)}>
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />
          <div className="absolute inset-0 flex items-center justify-center z-20">
            <div className="size-16 rounded-full bg-white/90 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
              <svg className="size-8 text-gray-800 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
            </div>
          </div>
          <Overlay />
        </div>
      );
    }
    // Placeholder for photo
    return (
      <div className={`${base} bg-gradient-to-b from-purple-900/30 via-pink-900/20 to-gray-900 flex flex-col items-center justify-center gap-3 group cursor-pointer`}>
        <div className="size-16 rounded-2xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
          <Instagram className="size-8 text-white" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-gray-300">{emptyLabel ?? t('preview.addPhoto')}</p>
          <p className="text-xs text-gray-500 mt-0.5">1:1 · Square format</p>
        </div>
        <Overlay />
      </div>
    );
  }

  // ── REELS (default) ──

  // Uploaded image
  if (parsed.type === 'image' && parsed.directUrl && !mediaError) {
    return (
      <div className={`${base} bg-black`}>
        <img src={parsed.directUrl} alt={t('preview.uploaded')} className="w-full h-full object-cover" onError={() => setMediaError(true)} />
        <Overlay />
      </div>
    );
  }

  // Uploaded video
  if (parsed.type === 'video' && parsed.directUrl && !mediaError) {
    if (isPlaying) {
      return (
        <div className={`${base} bg-black`}>
          <UploadedVideoPlayer src={parsed.directUrl} className="absolute inset-0 w-full h-full object-cover" autoPlay muted />
          <Overlay />
        </div>
      );
    }
    return (
      <div className={`${base} bg-black group cursor-pointer`} onClick={() => setIsPlaying(true)}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={t('preview.videoThumbnail')} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-gray-900" />
        )}
        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
        <div className="absolute inset-0 flex items-center justify-center z-20">
          <div className="size-16 rounded-full bg-white/90 dark:bg-black/70 flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
            <svg className="size-8 text-gray-800 dark:text-white ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          </div>
        </div>
        <Overlay />
      </div>
    );
  }

  // Pre-fetched video URL (CreateCardModal)
  if (extractedVideoUrl && isPlaying) {
    return (
      <div className={`${base} bg-black`}>
        <video
          key={extractedVideoUrl}
          src={extractedVideoUrl}
          className="absolute inset-0 w-full h-full object-cover"
          controls
          autoPlay
          playsInline
          muted
          preload="metadata"
          onError={() => setVideoError('Video playback failed')}
        />
        {videoError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 z-20 gap-3">
            <div className="p-3 rounded-full bg-red-500/20 text-red-500">
              <AlertTriangle className="size-6" />
            </div>
            <p className="text-white text-sm font-medium">{t('preview.failedToPlay')}</p>
            <Button
              size="sm" variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
              onClick={() => { setVideoError(null); setIsPlaying(false); setTimeout(() => setIsPlaying(true), 100); }}
            >{t('analytics.common.retry')}</Button>
          </div>
        )}
        <Overlay />
      </div>
    );
  }

  // Instagram URL detected — show thumbnail + play button
  if (parsed.type === 'instagram') {
    return (
      <div className={`${base} bg-gradient-to-b from-gray-800 to-gray-900`}>
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={t('preview.reelThumbnail')} className="absolute inset-0 w-full h-full object-cover" onError={() => setThumbnailUrl(null)} />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/40 to-pink-900/40" />
        )}
        <div className="absolute inset-0 bg-black/30" />

        {videoError && !extractedVideoUrl && (
          <div className="absolute top-2 left-2 right-2 z-20 bg-amber-500/95 backdrop-blur-sm rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="size-4 text-white shrink-0 mt-0.5" />
            <p className="text-xs text-white font-medium leading-tight">{videoError}</p>
          </div>
        )}

        {isExtracting ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 z-10">
            <BrandLoader label={t('common.loading')} />
            <span className="text-xs text-white/80 font-medium">{t('preview.extractingVideo')}</span>
          </div>
        ) : (
          <button
            onClick={handlePlay}
            disabled={!boardId && !extractedVideoUrl}
            className="absolute inset-0 flex items-center justify-center z-10 group/play"
          >
            <div className="size-16 rounded-full bg-white/90 dark:bg-black/70 flex items-center justify-center shadow-xl group-hover/play:scale-110 transition-transform">
              <Play className="size-8 text-gray-800 dark:text-white ml-1" fill="currentColor" />
            </div>
          </button>
        )}
        <Overlay />
      </div>
    );
  }

  // Error state
  if (initialError && !parsed.embedUrl) {
    return (
      <div className={`${base} bg-gray-100 dark:bg-gray-900 flex flex-col items-center justify-center gap-3 p-4`}>
        <AlertCircle className="size-8 text-red-500" />
        <p className="text-xs text-gray-500 text-center">{initialError}</p>
      </div>
    );
  }

  // Placeholder (reels)
  return (
    <div className={`${base} bg-gradient-to-b from-purple-900/30 via-pink-900/20 to-gray-900 flex flex-col items-center justify-center gap-3 group cursor-pointer`}>
      <div className="size-16 rounded-2xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
        <Instagram className="size-8 text-white" />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium text-gray-300">{emptyLabel ?? t('preview.addInstagramUrl')}</p>
        <p className="text-xs text-gray-500 mt-0.5">9:16 · Reel format</p>
      </div>
      <Overlay />
    </div>
  );
}
